import { Box, Typography } from "@mui/material";
import { NotificationsPlugin } from "@pimo/notifications-plugin-v2";
import type {
  App,
  Plugin,
  Route,
  UniversalAppState,
  View,
} from "@pimo/pimo-app-builder";
import {
  Chip,
  DefaultOverlayPartialAppState,
  PMButton,
  PMPopUp,
} from "@pimo/pimo-components";
import { DialogLayout } from "@pimo/pimo-components/src/lib/layouts";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { v4 as uuidv4 } from "uuid";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  ITEM_NAMES,
  Profile,
  Project,
  PROJECT_NAMES,
} from "vmd-types";
import {
  AUDIT_VIEW_CONFIG,
  PMO_VIEW_CONFIG,
  ROLLOUT_VIEW_CONFIG,
  validateConfiguration,
  VIEW_NAMES,
} from "vmd-utils";

// import ReactCountryFlag from "react-country-flag";
import { TestAppState } from "../app";
import { AntSwitch } from "../components/components/ant-switch";
import { ItemBasicFieldsForm } from "../components/define-items-data-model/items-basic-fields-form";
import { ProjectsBasicFieldsForm } from "../components/define-projects-data-model/projects-basic-fields-form";
import { ReportingFieldsForm } from "../components/define-reporting-data/reporting-data-fields-form";
import { ExcelImportPopup } from "../components/excel-import-popup";
import { ItemManagerPage } from "../components/manage-items/manage-subentities";
import { ManageOEsSettingsPage } from "../components/manage-projects/manage-main-entities-page";
import { ManageUsersPage } from "../components/manage-users/manage-users";
import { ReportingPeriodPage } from "../components/reporting-period";
import { ReportingStructurePage } from "../components/reporting-structure";
import { ReportingUseCasePage } from "../components/reporting-use-case";
import { APP_ROUTES } from "../constants";
import { PURPLE_BADGE_BACKGROUND } from "../constants/colors";
import { importItems, parseExcelHeaders } from "../helpers/excel-importer";
import { fetchDashboard } from "../helpers/fetch-dashboard";
import { fetchOverlay } from "../helpers/fetch-overlay";
import {
  clearSettings,
  fetchSettings,
  updateSettings,
} from "../helpers/fetch-settings";
import { saveUserSettings } from "../helpers/fetch-user-settings";
import { createViewConfiguration } from "../helpers/fetch-view-configuration";
import { invalidateAppQueries } from "../helpers/query-cache";
import { showSnackbar } from "../helpers/show-snackbar";
import { theme } from "../theme";
// eslint-disable-next-line
export interface SettingsPluginState {}

export type SettingsViewName = (typeof VIEW_NAMES)[number];

export class SettingsPlugin implements Plugin<
  TestAppState,
  SettingsPluginState,
  "viewname"
> {
  route?: Route<"viewname", TestAppState>;
  private settingsView?: View<TestAppState, DialogLayout>;
  private app?: App<TestAppState>;

  onRegister(app: App<TestAppState>): void {
    this.app = app;

    this.settingsView = app.createView({
      name: "Settings",
      layout: new DialogLayout(),
    });

    this.buildRoute();
    this.buildReportingStructureView();
    this.buildReportingPeriodView();
    this.buildProjectsDataModelView();
    this.buildItemsDataModelView();
    this.buildManageReprtingDataView();
    this.buildManageProjectsView();
    this.buildManageActivitiesView();
    this.buildManageUsersView();
    this.buildExcelMappingDialog();
    this.setupExcelImportHandlers();
    this.setupOeUploadHandlers();
    this.setupDistributionImportRefreshHandlers();
    this.buildNotificationsView();

    this.buildReportingUseCaseView();

    this.settingsView?.mapLayoutState((state) => {
      const resourceLockPlugin =
        this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      const itemName =
        ITEM_NAMES.find(
          (entry) => entry.single === state.settings?.configuration?.itemName
        ) ?? ITEM_NAMES[0];
      const projectName =
        PROJECT_NAMES.find(
          (entry) => entry.single === state.settings?.configuration?.projectName
        ) ?? PROJECT_NAMES[0];

      const hasUnpublishedChanges =
        !state?.editSettingsForm?.configuration?.isPublished;
      const hasCreatedFields = state.editSettingsForm?.hasCreatedFields;
      const hasDeletedFields = state.editSettingsForm?.hasDeletedFields;

      const entries = [
        {
          title:
            getLocalizedMessage(
              state.language || "en",
              "settingsPage.generalData"
            ) || "General Data",
          icon: "CheckCircleOutline",
          iconProps: {
            color: theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.reportingStructure,
              title:
                getLocalizedMessage(
                  state.language || "en",
                  "settingsPage.reportingStructure"
                ) || "Reporting Structure",
              viewname: VIEW_NAMES[0],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[0])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[0])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.reportingPeriod,
              title:
                getLocalizedMessage(
                  state.language || "en",
                  "settingsPage.reportingPeriod"
                ) || "Reporting Period",
              viewname: VIEW_NAMES[1],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[1])
                ? "Error"
                : undefined,

              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[1])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title:
            getLocalizedMessage(
              state.language || "en",
              "settingsPage.dataModel"
            ) || "Data Model",
          icon: "CheckCircleOutline",
          iconProps: {
            color: theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.manageProjectsDataModel,
              title: `${
                getLocalizedMessage(
                  state.language || "en",
                  "dataModel.masterDataProject"
                ) || "Master Data"
              } ${projectName.single}`,
              viewname: VIEW_NAMES[2],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[2])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[2])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.manageItemsDataModel,
              title: `${
                getLocalizedMessage(
                  state.language || "en",
                  "dataModel.masterDataItem"
                ) || "Master Data"
              } ${itemName.single}`,
              viewname: VIEW_NAMES[3],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[3])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[3])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.manageReportingData,
              title:
                getLocalizedMessage(
                  state.language || "en",
                  "settingsPage.reporting"
                ) || "Reporting",
              viewname: VIEW_NAMES[4],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[4])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[4])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title:
            getLocalizedMessage(
              state.language || "en",
              "settingsPage.content"
            ) || "Content",
          icon: "CheckCircleOutline",
          iconProps: {
            color: theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.manageMainEntities,
              title: `${
                getLocalizedMessage(
                  state.language || "en",
                  "settingsPage.manageProjects"
                ) || "Manage"
              }`.replace("{{projectName}}", projectName.plural),
              viewname: VIEW_NAMES[5],

              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[5])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[5])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
            {
              link: APP_ROUTES.settings.manageSubentities,

              title: `${
                getLocalizedMessage(
                  state.language || "en",
                  "settingsPage.manageItems"
                ) || "Manage"
              }`.replace("{{itemName}}", itemName.plural),
              viewname: VIEW_NAMES[6],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[6])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[6])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title: "User Management",
          icon: "CheckCircleOutline",
          iconProps: {
            color: theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.manageUsers,
              title: "Manage Access",
              viewname: VIEW_NAMES[7],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[7])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[7])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title: "Manage Notifications",
          icon: "CheckCircleOutline",
          iconProps: {
            color: theme.palette.grey["400"],
          },
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.notifications,
              title: "Notifications",
              viewname: VIEW_NAMES[8],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[8])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[8])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
        {
          title:
            getLocalizedMessage(
              state.language || "en",
              "settingsPage.generalData"
            ) || "For testing",
          level: 0,
          items: [
            {
              link: APP_ROUTES.settings.reportingUseCase,
              title: "Reporting Use Case",
              viewname: VIEW_NAMES[9],
              level: 1,
              icon: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[9])
                ? "Error"
                : undefined,
              iconProps: Object.entries(
                state.editSettingsForm?.validationErrors ?? {}
              ).find(([, err]) => err?.tabContainingErrors === VIEW_NAMES[9])
                ? {
                    color: theme.palette.error.main,
                  }
                : undefined,
            },
          ],
        },
      ];

      const useCaseName = state.settings?.configuration?.useCase
        ? getLocalizedMessage(
            state.language || "en",
            `useCase.${state.settings?.configuration?.useCase}`
          )
        : undefined;

      return {
        entries,
        title: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {getLocalizedMessage(
              state.language || "en",
              "settingsPage.reportingSettings"
            ) || "Reporting Settings"}
            {useCaseName && (
              <Chip
                sx={{
                  backgroundColor: PURPLE_BADGE_BACKGROUND,
                  fontWeight: 500,
                  fontSize: "12px",
                }}
              >
                {`Use Case: ${useCaseName}`}
              </Chip>
            )}
          </Box>
        ),
        icon: {
          alt: "Settings",
          small: "pimo-logo.svg",
          big: "pimo-logo.svg",
        },
        nodes: [
          <Box
            key="lang-save-row"
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            {/* Language selector hidden - default language is English */}
            {/* <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel id="language-select-label">
                {getLocalizedMessage(
                  state.language || "en",
                  "settingsPage.language"
                ) || "Language"}
              </InputLabel>
              <Select
                labelId="language-select-label"
                id="language-select"
                value={state.language || "en"}
                label={
                  getLocalizedMessage(
                    state.language || "en",
                    "settingsPage.language"
                  ) || "Language"
                }
                onChange={async (e: SelectChangeEvent<string>) => {
                  this.app?.patchAppState({
                    language: e.target.value as "en" | "de",
                  });

                  const freshSettings = await fetchSettings(
                    e.target.value as "en" | "de"
                  );

                  this.app?.patchAppState({
                    settings: {
                      projects: freshSettings.projects,
                      basicItems: freshSettings.basicItems ?? [],
                      configuration: freshSettings.configuration,
                    },
                    editSettingsForm: {
                      projects: freshSettings.projects,
                      basicItems: freshSettings.basicItems ?? [],
                      configuration: freshSettings.configuration,
                      users: freshSettings.users ?? [],
                    },
                  });
                }}
                renderValue={(value) =>
                  value === "de" ? (
                    <>
                      <ReactCountryFlag
                        countryCode="DE"
                        svg
                        style={{
                          width: "1.5em",
                          height: "1.5em",
                          marginRight: 8,
                        }}
                      />
                      {getLocalizedMessage(
                        state.language || "en",
                        "settingsPage.german"
                      ) || "Deutsch"}
                    </>
                  ) : (
                    <>
                      <ReactCountryFlag
                        countryCode="GB"
                        svg
                        style={{
                          width: "1.5em",
                          height: "1.5em",
                          marginRight: 8,
                        }}
                      />
                      {getLocalizedMessage(
                        state.language || "en",
                        "settingsPage.english"
                      ) || "English"}
                    </>
                  )
                }
              >
                <MenuItem value="en">
                  <ReactCountryFlag
                    countryCode="GB"
                    svg
                    style={{ width: "1.5em", height: "1.5em", marginRight: 8 }}
                  />
                  {getLocalizedMessage(
                    state.language || "en",
                    "settingsPage.english"
                  ) || "English"}
                </MenuItem>
                <MenuItem value="de">
                  <ReactCountryFlag
                    countryCode="DE"
                    svg
                    style={{ width: "1.5em", height: "1.5em", marginRight: 8 }}
                  />
                  {getLocalizedMessage(
                    state.language || "en",
                    "settingsPage.german"
                  ) || "Deutsch"}
                </MenuItem>
              </Select>
            </FormControl> */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">Activate Report</Typography>
              <AntSwitch
                sx={{ marginLeft: 1 }}
                checked={
                  state.editSettingsForm?.configuration?.isReportingEnabled ??
                  false
                }
                onChange={(e) => {
                  this.app?.patchAppState({
                    editSettingsForm: {
                      ...state.editSettingsForm,
                      configuration: {
                        ...state.editSettingsForm?.configuration,
                        isReportingEnabled: e.target.checked,
                      } as Configuration,
                      enableSaveButton: true,
                    },
                  });
                }}
              />
            </Box>
            <PMButton
              variant={
                state.editSettingsForm?.enableSaveButton
                  ? "contained"
                  : "disabled"
              }
              size="medium"
              text={
                getLocalizedMessage(state.language || "en", "saveButton") ||
                "Save"
              }
              onClick={() => {
                this.saveSettings(false);
              }}
            />
            <PMButton
              variant={
                state.editSettingsForm?.configuration?.isPublished
                  ? "disabled"
                  : "report"
              }
              size="medium"
              text={"Publish"}
              onClick={() => {
                if (!this.app) return;
                const state = this.app.getAppState();

                const skipChangedFieldDialog =
                  state.userSettings?.skipChangedFieldDialog ?? false;
                const hasCreatedFields =
                  state.editSettingsForm?.hasCreatedFields;
                const hasDeletedFields =
                  state.editSettingsForm?.hasDeletedFields;

                if (
                  !skipChangedFieldDialog &&
                  (hasCreatedFields || hasDeletedFields)
                ) {
                  this.app?.patchAppState({
                    showModifiedFieldsDialog: true,
                  });
                  return;
                }

                this.saveSettings(true);
              }}
            />
            <PMButton
              variant="contained"
              size="medium"
              text={
                getLocalizedMessage(state.language || "en", "clearAll") ??
                "Clear All"
              }
              onClick={async () => {
                this.app?.patchAppState({
                  showClearAllDataDialog: true,
                });
              }}
            />

            {/* Clear All Data Dialog */}
            <PMPopUp
              open={!!state.showClearAllDataDialog}
              title={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.title.clearAllSettings"
                ) || "Clear All Data"
              }
              description={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.message.clearAllSettings"
                ) ||
                "Are you sure you want to clear all settings data? This action cannot be undone."
              }
              onClose={() => {
                this.app?.patchAppState({
                  showClearAllDataDialog: false,
                });
              }}
              maxWidth="sm"
              cancelButtonColor="white"
              cancelButtonText="Cancel"
              cancelButtonFireEvent={() => {
                this.app?.patchAppState({
                  showClearAllDataDialog: false,
                });
              }}
              confirmationButtonText={"Clear All"}
              confirmationButtonColor="blue"
              confirmationButtonFireEvent={async () => {
                const [settings, overlay, dashboard] = await Promise.all([
                  clearSettings({
                    users: state.editSettingsForm?.users ?? [],
                  }),
                  fetchOverlay(),
                  fetchDashboard(),
                ]);
                showSnackbar(
                  this.app as unknown as App<
                    UniversalAppState & DefaultOverlayPartialAppState
                  >,
                  "All settings data have been cleared.",
                  "success"
                );
                this.app?.patchAppState({
                  showClearAllDataDialog: false,
                  overlay,
                  dashboard,
                  settings: {
                    projects: settings?.projects ?? ([] as Project[]),
                    basicItems: settings?.basicItems ?? ([] as BasicItem[]),
                    configuration:
                      settings?.configuration ?? ({} as Configuration),
                  },
                  editSettingsForm: {
                    projects: settings?.projects ?? ([] as Project[]),
                    basicItems: settings?.basicItems ?? ([] as BasicItem[]),
                    configuration:
                      settings?.configuration ?? ({} as Configuration),
                  },
                });
              }}
            />
            {/* Unsaved/Unpublished changes dialog */}
            <PMPopUp
              open={!!state.showUnsavedSettingsDialog}
              title={
                hasUnpublishedChanges
                  ? getLocalizedMessage(
                      state.language || "en",
                      "dialog.title.unpublishedChanges"
                    ) || "Unpublished Changes"
                  : getLocalizedMessage(
                      state.language || "en",
                      "dialog.title.unsavedChanges"
                    ) || "Unsaved Changes"
              }
              description={
                hasUnpublishedChanges
                  ? getLocalizedMessage(
                      state.language || "en",
                      "dialog.message.unpublishedChanges",
                      { entity: "settings" }
                    ) ||
                    "You have made changes that have been saved but not published. If you close the settings, the changes will not be displayed in the workspace."
                  : getLocalizedMessage(
                      state.language || "en",
                      "dialog.message.unsavedChanges",
                      { entity: "settings" }
                    ) ||
                    "You have made changes that have not been saved. If you close the settings, these changes will be lost."
              }
              onClose={() =>
                this.app?.patchAppState({
                  showUnsavedSettingsDialog: false,
                })
              }
              maxWidth="sm"
              cancelButtonText={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.button.cancel"
                ) || "Cancel"
              }
              cancelButtonFireEvent={() =>
                this.app?.patchAppState({
                  showUnsavedSettingsDialog: false,
                })
              }
              cancelButtonColor="white"
              confirmationButtonColor="blue"
              confirmationButtonText={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.button.continue"
                ) || "Continue"
              }
              confirmationButtonFireEvent={async () => {
                this.app?.patchAppState({
                  showUnsavedSettingsDialog: false,
                });

                this.app?.navigate(APP_ROUTES.dashboard);
                resourceLockPlugin?.unlock("settings");
                const [overlay] = await Promise.all([fetchOverlay()]);

                this.app?.patchAppState({
                  overlay,
                });
              }}
            />
            {/* Modified Fields Dialog */}
            <PMPopUp
              open={!!state.showModifiedFieldsDialog}
              title={this.getModifiedFieldsDialogTitle(
                hasCreatedFields,
                hasDeletedFields
              )}
              description={this.getModifiedFieldsDialogText(
                hasCreatedFields,
                hasDeletedFields
              )}
              onClose={() =>
                this.app?.patchAppState({
                  showModifiedFieldsDialog: false,
                })
              }
              maxWidth="sm"
              cancelButtonText={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.button.cancel"
                ) || "Cancel"
              }
              cancelButtonFireEvent={() =>
                this.app?.patchAppState({
                  showModifiedFieldsDialog: false,
                })
              }
              cancelButtonColor="white"
              confirmationButtonColor="blue"
              confirmationButtonText={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.button.continue"
                ) || "Continue"
              }
              customCheckboxText={
                getLocalizedMessage(
                  state.language || "en",
                  "dialog.message.dontShowAgain"
                ) || "Don't ask me again"
              }
              confirmationButtonFireEvent={async (_, data) => {
                const dontShowAgain = data?.customCheckboxChecked ?? false;

                dontShowAgain &&
                  saveUserSettings({ skipChangedFieldDialog: true });

                this.saveSettings(true);
                this.app?.patchAppState({
                  showModifiedFieldsDialog: false,
                  userSettings: {
                    ...state.userSettings,
                    skipChangedFieldDialog: dontShowAgain,
                  },
                });
              }}
            />
          </Box>,
        ],
      };
    });

    this.settingsView?.on("dialog:close", async () => {
      const resourceLockPlugin =
        this.app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      const state = this.app?.getAppState();
      const hasUnsavedChanges = state?.editSettingsForm?.enableSaveButton;
      const hasUnpublishedChanges =
        !state?.editSettingsForm?.configuration?.isPublished;

      if (hasUnsavedChanges || hasUnpublishedChanges) {
        this.app?.patchAppState({
          showUnsavedSettingsDialog: true,
        });
        return;
      }

      this.app?.navigate(APP_ROUTES.dashboard);
      resourceLockPlugin?.unlock("settings");
      const [overlay] = await Promise.all([fetchOverlay()]);

      this.app?.patchAppState({
        overlay,
      });
    });
    this.settingsView?.on("dialog:navigate", (payload) => {
      if (!payload.payload) return;
      this.app?.navigate(payload.payload.currentPath);
    });
  }

  private buildRoute() {
    if (!this.app || !this.settingsView) return;

    this.route = this.app.createRoute<"viewname">({
      path: "/settings/:viewname",
      view: this.settingsView,
    });
  }

  private buildReportingUseCaseView() {
    const reportingStructurePage = this.settingsView?.addComponent({
      component: ReportingUseCasePage,
      layoutProps: { viewname: VIEW_NAMES[9] },
    });

    reportingStructurePage?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        projects: state.settings?.projects ?? [],
        language: state.language || "en",
      };
    });

    reportingStructurePage?.on("update:view-configuration", async (payload) => {
      if (!payload || !this.app) return;

      const configMap = {
        audit: AUDIT_VIEW_CONFIG,
        rollout: ROLLOUT_VIEW_CONFIG,
        pmo: PMO_VIEW_CONFIG,
        // Distribution is rollout-based; reuse the rollout layout as its base (channel tabs are built dynamically).
        distribution: ROLLOUT_VIEW_CONFIG,
      } as const;

      const viewConfig = configMap[payload.payload as keyof typeof configMap];
      if (!viewConfig) return;

      const { reportLayout } = this.app.getAppState();

      const currentDisplayTabs = reportLayout?.displayTabs ?? true;

      const { tabs } = viewConfig["project-common"];

      this.app.patchAppState({
        dashboardLayout: {
          tabs: viewConfig.dashboard.tabs,
        },
        reportLayout: {
          displayTabs: currentDisplayTabs,
          tabs,
        },
      });

      await createViewConfiguration({
        tabs: viewConfig["project-common"].tabs,
        canEdit: reportLayout?.canEdit ?? true,
        displayTabs: currentDisplayTabs,
        type: "project-common",
      });

      await createViewConfiguration({
        tabs: viewConfig.dashboard.tabs,
        canEdit: reportLayout?.canEdit ?? true,
        displayTabs: currentDisplayTabs,
        type: "dashboard",
      });
    });
  }

  private buildReportingStructureView() {
    const reportingStructurePage = this.settingsView?.addComponent({
      component: ReportingStructurePage,
      layoutProps: { viewname: VIEW_NAMES[0] },
    });

    reportingStructurePage?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language || "en",
      };
    });
  }

  private buildReportingPeriodView() {
    const reportingPeriodPage = this.settingsView?.addComponent({
      component: ReportingPeriodPage,
      layoutProps: { viewname: VIEW_NAMES[1] },
    });

    reportingPeriodPage?.mapState(() => {
      return {};
    });
  }

  private buildProjectsDataModelView() {
    const projectBasicFieldsForm = this.settingsView?.addComponent({
      component: ProjectsBasicFieldsForm,
      layoutProps: { viewname: VIEW_NAMES[2] },
    });

    projectBasicFieldsForm?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language || "en",
        setUpdatedFieldsFlag: (
          change: "FieldActivated" | "FieldDeactivated"
        ) => {
          this.app?.patchAppState({
            editSettingsForm: {
              ...state.editSettingsForm,
              ...(change === "FieldActivated"
                ? { hasCreatedFields: true }
                : { hasDeletedFields: true }),
              enableSaveButton: true,
            },
          });
        },
      };
    });
  }

  private buildItemsDataModelView() {
    const itemBasicFieldsForm = this.settingsView?.addComponent({
      component: ItemBasicFieldsForm,
      layoutProps: { viewname: VIEW_NAMES[3] },
    });

    itemBasicFieldsForm?.mapState((state) => {
      return {
        configuration: state.settings?.configuration ?? ({} as Configuration),
        language: state.language || "en",
        setUpdatedFieldsFlag: (
          change: "FieldActivated" | "FieldDeactivated"
        ) => {
          this.app?.patchAppState({
            editSettingsForm: {
              ...state.editSettingsForm,
              ...(change === "FieldActivated"
                ? { hasCreatedFields: true }
                : { hasDeletedFields: true }),
              enableSaveButton: true,
            },
          });
        },
      };
    });
  }

  private buildManageReprtingDataView() {
    const projectBasicFieldsForm = this.settingsView?.addComponent({
      component: ReportingFieldsForm,
      layoutProps: { viewname: VIEW_NAMES[4] },
    });

    projectBasicFieldsForm?.mapState((state) => {
      return {
        configuration: state.settings?.configuration,
        language: state.language || "en",
        setUpdatedFieldsFlag: (
          change: "FieldActivated" | "FieldDeactivated"
        ) => {
          this.app?.patchAppState({
            editSettingsForm: {
              ...state.editSettingsForm,
              ...(change === "FieldActivated"
                ? { hasCreatedFields: true }
                : { hasDeletedFields: true }),
              enableSaveButton: true,
            },
          });
        },
      };
    });
  }
  private buildManageProjectsView() {
    const manageProjectsView = this.settingsView?.addComponent({
      component: ManageOEsSettingsPage,
      layoutProps: { viewname: VIEW_NAMES[5] },
    });
    manageProjectsView?.mapState((state) => {
      return {
        projects: state.settings?.projects ?? [],
        configuration: state.settings?.configuration,
        language: state.language || "en",
      };
    });
  }
  private buildManageActivitiesView() {
    const manageSubentitiesView = this.settingsView?.addComponent({
      component: ItemManagerPage,
      layoutProps: { viewname: VIEW_NAMES[6] },
    });
    manageSubentitiesView?.mapState((state) => ({
      basicItems: state.settings?.basicItems ?? ([] as BasicItem[]),
      projects: state.settings?.projects ?? [],
      configuration: state.settings?.configuration,
      language: state.language || "en",
    }));
  }
  private buildManageUsersView() {
    const manageAccessView = this.settingsView?.addComponent({
      component: ManageUsersPage,
      layoutProps: { viewname: VIEW_NAMES[7] },
    });
    manageAccessView?.mapState((state) => ({
      configuration: state.settings?.configuration,
      language: state.language,
    }));
  }

  private updateImportDialog(state: {
    excelHeaders?: string[];
    itemFields?: string[];
    open?: boolean;
    file?: File | null;
    isLoading?: boolean;
    excelData?: unknown[][];
  }) {
    const currentState = this.app?.getAppState();
    this.app?.patchAppState({
      importDialog: {
        excelHeaders: [],
        itemFields: [],
        open: false,
        file: null,
        suggestedMapping: {},
        isLoading: false,
        excelData: [],
        ...currentState?.importDialog,
        ...state,
      },
    });
  }

  private buildExcelMappingDialog() {
    const mappingDialog = this.settingsView?.addComponent({
      component: ExcelImportPopup,
      layoutProps: { viewname: VIEW_NAMES[6] },
    });

    mappingDialog?.mapState((state) => ({
      excelHeaders: state.importDialog?.excelHeaders ?? [],
      itemFields: state.importDialog?.itemFields ?? [],
      open: state.importDialog?.open ?? false,
      suggestedMapping: state.importDialog?.suggestedMapping ?? {},
      isLoading: state.importDialog?.isLoading ?? false,
      excelData: state.importDialog?.excelData ?? [],
      language: state.language || "en",
      onClose: () => {
        this.updateImportDialog({ open: false });
      },
    }));

    mappingDialog?.on(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      "excel-import:save" as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      async (event: any) => {
        let { mapping = {} } = event.payload ?? {};
        const { headerRowIndex = 0, dataStartRowIndex = 1 } =
          event.payload ?? {};
        const currentState = this.app?.getAppState();
        const file = currentState?.importDialog?.file;
        if (!file) return;

        if (Object.keys(mapping).length === 0) {
          const suggestedMapping =
            currentState?.importDialog?.suggestedMapping ?? {};
          const itemFields = currentState?.importDialog?.itemFields ?? [];
          const excelHeaders = currentState?.importDialog?.excelHeaders ?? [];

          const newMapping: Record<string, string> = {};
          excelHeaders.forEach((header) => {
            const suggestedValue = suggestedMapping[header];
            if (suggestedValue && itemFields.includes(suggestedValue)) {
              newMapping[header] = suggestedValue;
            }
          });

          mapping = newMapping;
        }

        this.updateImportDialog({
          isLoading: true,
        });

        try {
          const importResult = await importItems(
            file,
            mapping,
            undefined,
            headerRowIndex,
            dataStartRowIndex
          );

          if (!importResult || typeof importResult !== "object") {
            throw new Error("Invalid import result received from server");
          }

          const currentForm = currentState.editSettingsForm;
          const existingMeasures = currentForm?.basicItems ?? [];
          const existingFindings = currentForm?.projects ?? [];

          const stagedMeasures = importResult.stagedMeasures ?? [];
          const stagedFindings = importResult.stagedFindings ?? [];

          this.app?.patchAppState({
            editSettingsForm: {
              ...currentForm,
              basicItems: [...existingMeasures, ...stagedMeasures],
              projects: [...existingFindings, ...stagedFindings],
            },
          });

          this.updateImportDialog({ open: false });
        } catch (error) {
          console.error("[ExcelImport] Import failed:", error);
          this.updateImportDialog({
            open: true,
            isLoading: false,
          });
        }
      }
    );
  }

  private setupExcelImportHandlers() {
    const handleExcelFileSelected = async (event: CustomEvent) => {
      const file = event.detail?.file as File;
      console.debug("[ExcelImport] File selected event received", {
        hasFile: !!file,
        name: file?.name,
      });
      if (!file) return;

      this.updateImportDialog({ open: true, file, isLoading: true });

      try {
        const result = await parseExcelHeaders(file);
        console.debug("[ExcelImport] Parsed headers", {
          excelHeaders: result.excelHeaders,
          itemFields: result.itemFields,
          excelDataRows: result.excelData?.length || 0,
        });
        this.updateImportDialog({
          excelHeaders: result.excelHeaders,
          itemFields: result.itemFields,
          excelData: result.excelData,
          open: true,
          file,
          isLoading: false,
        });
      } catch (error) {
        console.error("[ExcelImport] Failed to parse Excel headers:", error);
        this.updateImportDialog({ open: true, isLoading: false });
      }
    };

    document.removeEventListener(
      "excel-file-selected",
      handleExcelFileSelected as unknown as EventListener
    );
    document.addEventListener(
      "excel-file-selected",
      handleExcelFileSelected as unknown as EventListener
    );
  }

  private setupOeUploadHandlers() {
    const handleOeUploadCreate = async (event: CustomEvent) => {
      if (!this.app) return;
      const names = (event.detail?.names as string[]) ?? [];
      if (!names.length) return;

      const state = this.app.getAppState();
      const projectName =
        PROJECT_NAMES.find(
          (entry) =>
            entry.single ===
            (state.editSettingsForm?.configuration?.projectName ?? "Project")
        ) ?? PROJECT_NAMES[0];

      const existingProjects = state.editSettingsForm?.projects ?? [];
      const existingNames = new Set(
        existingProjects
          .filter((project) => project.flag !== "delete")
          .map((project) => (project.name ?? "").trim().toLowerCase())
      );

      // Skip names that already exist or repeat within the uploaded file.
      const seen = new Set<string>();
      const newOEs: Project[] = [];
      for (const rawName of names) {
        const name = (rawName ?? "").trim();
        if (!name) continue;
        const key = name.toLowerCase();
        if (existingNames.has(key) || seen.has(key)) continue;
        seen.add(key);
        newOEs.push({
          id: uuidv4(),
          name,
          customId: name,
          description: "",
          owner: "",
          spentBudget: 0,
          flag: "create",
        });
      }

      const skipped = names.length - newOEs.length;
      const snackbarApp = this.app as unknown as App<
        UniversalAppState & DefaultOverlayPartialAppState
      >;

      if (newOEs.length === 0) {
        showSnackbar(
          snackbarApp,
          `No new ${projectName.plural} to create — all ${names.length} already exist.`,
          "info"
        );
        return;
      }

      this.app.patchAppState({
        editSettingsForm: {
          ...state.editSettingsForm,
          projects: [...existingProjects, ...newOEs],
          enableSaveButton: true,
        },
      });

      await this.saveSettings(false);

      // saveSettings bails on validation errors; only claim success when there are none.
      const updatedState = this.app.getAppState();
      const hasValidationErrors =
        Object.keys(updatedState.editSettingsForm?.validationErrors ?? {})
          .length > 0;
      if (hasValidationErrors) return;

      showSnackbar(
        snackbarApp,
        `Created ${newOEs.length} ${
          newOEs.length === 1 ? projectName.single : projectName.plural
        }${skipped > 0 ? ` (${skipped} skipped as duplicates)` : ""}.`,
        "success"
      );
    };

    document.removeEventListener(
      "oe-upload:create",
      handleOeUploadCreate as unknown as EventListener
    );
    document.addEventListener(
      "oe-upload:create",
      handleOeUploadCreate as unknown as EventListener
    );
  }

  private setupDistributionImportRefreshHandlers() {
    const handleDistributionImportRefresh = async () => {
      if (!this.app) return;

      const state = this.app.getAppState();
      const language = state.language || "en";

      // Invalidate caches before refetching to ensure we get fresh data
      await invalidateAppQueries(["settings", language]);
      await invalidateAppQueries(["entity-configuration"]);
      await invalidateAppQueries(["projects"]);

      const freshSettings = await fetchSettings(language);

      this.app.patchAppState({
        settings: {
          projects: freshSettings.projects,
          basicItems: freshSettings.basicItems ?? [],
          configuration: freshSettings.configuration,
        },
        editSettingsForm: {
          ...state.editSettingsForm,
          projects: freshSettings.projects,
          basicItems: freshSettings.basicItems ?? [],
          configuration: freshSettings.configuration,
          toolUsers: freshSettings.toolUsers ?? [],
          clientUsers: freshSettings.clientUsers ?? [],
          enableSaveButton: false,
          validationErrors: {},
        },
      });
    };

    document.removeEventListener(
      "distribution-import:refresh",
      handleDistributionImportRefresh as unknown as EventListener
    );
    document.addEventListener(
      "distribution-import:refresh",
      handleDistributionImportRefresh as unknown as EventListener
    );
  }

  private buildNotificationsView() {
    if (!this.settingsView || !this.route) return;

    const notificationsPlugin = this.app?.getPluginByName<
      NotificationsPlugin<TestAppState>
    >("NotificationsPlugin");

    notificationsPlugin?.renderNotificationsManager({
      view: this.settingsView,
      route: this.route as Route,
      layoutProps: { viewname: VIEW_NAMES[8] },
    });
  }

  private saveSettings = async (publish: boolean) => {
    if (!this.app) return;

    const state = this.app?.getAppState();

    const validation = validateConfiguration(
      state.editSettingsForm?.configuration ?? ({} as Configuration),
      state.editSettingsForm?.projects ?? [],
      state.editSettingsForm?.basicItems ?? [],
      state.editSettingsForm?.toolUsers ?? [],
      state.editSettingsForm?.clientUsers ?? []
    );

    if (Object.keys(validation ?? {}).length) {
      this.app?.patchAppState({
        editSettingsForm: {
          ...(this.app?.getAppState().editSettingsForm ??
            state.editSettingsForm),
          validationErrors: validation,
        },
      });
      return;
    }
    const [settings] = await Promise.all([
      updateSettings({
        projects: state.editSettingsForm?.projects ?? [],
        basicItems: state.editSettingsForm?.basicItems ?? [],
        trackingItemsApplyToAllProjects:
          state.editSettingsForm?.configuration
            ?.trackingItemsApplyToAllProjects ?? false,
        configuration: {
          ...state.editSettingsForm?.configuration,
          isPublished: publish,
        } as Configuration,
        toolUsers: state.editSettingsForm?.hasUpdatedUsers
          ? (state.editSettingsForm?.toolUsers ?? [])
          : undefined,
        clientUsers: state.editSettingsForm?.clientUsers ?? [],
        hasUpdatedUsers: state.editSettingsForm?.hasUpdatedUsers,
        notificationSettings: {
          notifications: state.changedNotifications ?? [],
          scheduledNotifications: state.changedScheduledNotifications ?? [],
          published: publish,
        },
      }),
    ]);

    if (!settings) return;

    if ("validationErrors" in settings) {
      this.app?.patchAppState({
        editSettingsForm: {
          ...(this.app?.getAppState().editSettingsForm ??
            state.editSettingsForm),
          validationErrors: settings.validationErrors as Record<
            string,
            ExtendedFieldError
          >,
        },
      });
      return;
    }
    showSnackbar(
      this.app as unknown as App<
        UniversalAppState & DefaultOverlayPartialAppState
      >,
      `Settings have been saved${publish ? " and published" : ""}.`,
      "success"
    );
    this.app?.patchAppState({
      overlay: {
        projects: settings?.projects ?? ([] as Project[]),
      },
      settings: {
        projects: settings?.projects ?? ([] as Project[]),
        basicItems: settings?.basicItems ?? ([] as BasicItem[]),
        configuration: settings?.configuration ?? ({} as Configuration),
      },
      editSettingsForm: {
        projects: settings?.projects ?? ([] as Project[]),
        basicItems: settings?.basicItems ?? ([] as BasicItem[]),
        configuration: settings?.configuration ?? ({} as Configuration),
        toolUsers: settings?.toolUsers ?? ([] as Profile[]),
        clientUsers: settings?.clientUsers ?? ([] as Profile[]),
        enableSaveButton: false,
        ...(publish
          ? {
              hasCreatedFields: false,
              hasDeletedFields: false,
            }
          : {
              hasCreatedFields:
                state.editSettingsForm?.hasCreatedFields ?? false,
              hasDeletedFields:
                state.editSettingsForm?.hasDeletedFields ?? false,
            }),
      },
      ...(settings?.notifications
        ? { allNotifications: settings?.notifications ?? [] }
        : {}),
      ...(settings?.scheduledNotifications
        ? {
            scheduledNotifications: settings.scheduledNotifications ?? [],
          }
        : {}),
    });
  };

  private getModifiedFieldsDialogText(
    hasCreatedFields?: boolean,
    hasDeletedFields?: boolean
  ) {
    if (hasCreatedFields && hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.message.dataFieldCreatedAndDeleted"
        ) ||
        "You have deactivated/deleted and activated/created a data field. Any activated/created fields are now integrated into the forms and the corresponding cards are made available in the component library. Any deactivated/deleted fields are no longer integrated into the forms and corresponding cards are no longer available in the component library."
      );
    } else if (hasCreatedFields && !hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.message.dataFieldActivatedOrCreated"
        ) ||
        "You have activated a data field or created a new data field. These fields are now integrated into the forms. Corresponding cards are made available in the component library. These still need to be integrated into the desired pages."
      );
    }

    return (
      getLocalizedMessage(
        this.app?.getAppState().language || "en",
        "dialog.message.dataFieldDeactivatedOrDeleted"
      ) ||
      "You have deactivated a data field or deleted a new data field. These fields are no longer integrated into the forms. Corresponding cards are no longer available in the component library and need to be removed from pages."
    );
  }

  private getModifiedFieldsDialogTitle(
    hasCreatedFields?: boolean,
    hasDeletedFields?: boolean
  ) {
    if (hasCreatedFields && hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.title.dataFieldCreatedAndDeleted"
        ) || "Data field created/activated and delted/deactivated"
      );
    } else if (hasCreatedFields && !hasDeletedFields) {
      return (
        getLocalizedMessage(
          this.app?.getAppState().language || "en",
          "dialog.title.dataFieldActivatedOrCreated"
        ) || "Data field activated or created"
      );
    }

    return (
      getLocalizedMessage(
        this.app?.getAppState().language || "en",
        "dialog.title.dataFieldDeactivatedOrDeleted"
      ) || "Data field deactivated or deleted"
    );
  }
}
